/**
 * ContiTavoli (read-only)
 * =======================
 * Vista cassa SENZA possibilità di operazioni. Mostra:
 *  - Tavoli aperti con dettaglio piatti + totali + cameriere
 *  - Filtro per sala
 *  - Auto-refresh ogni 6s + reazione a WS events
 * Accessibile a: admin / cashier / waiter.
 * NESSUN bottone Paga/Sconto/Modifica/Annulla — è esclusivamente di consultazione.
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useWS } from "@/contexts/WebSocketContext";
import { Eye, Users, Clock, ChefHat, ReceiptText } from "lucide-react";

const STATUS_BADGE = {
    in_attesa: { label: "In attesa", cls: "bg-elegant/20 text-elegant border-elegant/40" },
    in_cucina: { label: "In cucina", cls: "bg-blue-900/30 text-blue-300 border-blue-500/40" },
    in_preparazione: { label: "In prep.", cls: "bg-blue-900/30 text-blue-300 border-blue-500/40" },
    pronto: { label: "Pronto", cls: "bg-amber-900/30 text-amber-300 border-amber-500/40" },
    consegnato: { label: "Consegnato", cls: "bg-emerald-900/30 text-emerald-300 border-emerald-500/40" },
    annullato: { label: "Annullato", cls: "bg-red-900/30 text-red-300 border-red-500/40" },
};

const itemNet = (it) => {
    const extrasSum = (it.extras || []).reduce((s, e) => s + (parseFloat(e.price) || 0), 0);
    const gross = (it.price + extrasSum) * it.quantity;
    let net = gross;
    if (it.discount_pct) net = net * (1 - it.discount_pct / 100);
    if (it.discount_amt) net = Math.max(0, net - it.discount_amt);
    return { gross, net };
};

const orderTotal = (o) => {
    if (!o) return 0;
    let sub = (o.items || []).filter((it) => it.status !== "annullato").reduce((s, it) => s + itemNet(it).net, 0);
    if (o.discount_pct) sub = sub * (1 - o.discount_pct / 100);
    if (o.discount_amt) sub = Math.max(0, sub - o.discount_amt);
    return sub;
};

const elapsedMin = (iso) => {
    if (!iso) return 0;
    try {
        return Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    } catch { return 0; }
};

const ContiTavoli = () => {
    const [tables, setTables] = useState([]);
    const [orders, setOrders] = useState([]);
    const [rooms, setRooms] = useState([]);
    const [activeRoomId, setActiveRoomId] = useState("all");
    const { lastEvent } = useWS() || {};

    const load = async () => {
        try {
            const [t, o, r] = await Promise.all([api.get("/tables"), api.get("/orders"), api.get("/rooms")]);
            setTables(t.data);
            setOrders(o.data);
            setRooms(r.data);
        } catch (e) { /* silent — polling retry */ }
    };

    useEffect(() => { load(); const id = setInterval(load, 6000); return () => clearInterval(id); }, []);
    useEffect(() => {
        if (!lastEvent) return;
        if (["bill_requested", "table_updated", "payment_completed", "new_kitchen_order", "order_items_ready", "item_delivered"].includes(lastEvent.type)) load();
    }, [lastEvent]);

    const openOrders = useMemo(() => orders.filter((o) => o.status !== "chiuso"), [orders]);
    const tableById = useMemo(() => Object.fromEntries(tables.map((t) => [t.id, t])), [tables]);
    const roomById = useMemo(() => Object.fromEntries(rooms.map((r) => [r.id, r])), [rooms]);

    const filteredOrders = useMemo(() => {
        if (activeRoomId === "all") return openOrders;
        return openOrders.filter((o) => {
            const t = tableById[o.table_id];
            return t?.room_id === activeRoomId;
        });
    }, [openOrders, activeRoomId, tableById]);

    // Raggruppa per sala (quando "Tutte")
    const sortedOrders = useMemo(() => {
        return [...filteredOrders].sort((a, b) => {
            const ta = tableById[a.table_id];
            const tb = tableById[b.table_id];
            const na = parseInt(ta?.number, 10) || 999;
            const nb = parseInt(tb?.number, 10) || 999;
            return na - nb;
        });
    }, [filteredOrders, tableById]);

    const totalRevenuePending = useMemo(() => sortedOrders.reduce((s, o) => s + orderTotal(o), 0), [sortedOrders]);

    return (
        <div className="space-y-5" data-testid="conti-tavoli-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-brand flex items-center gap-1.5">
                        <Eye className="w-3 h-3" /> Sola consultazione
                    </div>
                    <h1 className="font-display text-3xl font-bold">Conti tavoli aperti</h1>
                    <div className="text-xs text-elegant mt-1">Visualizza i conti dei tavoli senza poter fare operazioni di pagamento.</div>
                </div>
                <div className="bg-surface border border-[#1f2c44] rounded-lg px-4 py-3">
                    <div className="text-[10px] uppercase tracking-wider text-elegant">Totale provvisorio aperto</div>
                    <div className="font-display text-2xl font-bold text-brand" data-testid="total-pending">€ {totalRevenuePending.toFixed(2)}</div>
                    <div className="text-[10px] text-elegant">{sortedOrders.length} tavoli</div>
                </div>
            </div>

            {/* Filter per sala */}
            {rooms.length > 1 && (
                <div className="flex items-center gap-1 flex-wrap" data-testid="room-tabs">
                    <button onClick={() => setActiveRoomId("all")}
                        data-testid="room-tab-all"
                        className={`h-8 px-3 rounded text-[11px] uppercase tracking-wider font-bold border ${
                            activeRoomId === "all" ? "border-brand bg-brand/20 text-brand" : "border-[#1f2c44] text-elegant hover:border-brand/60"
                        }`}>
                        Tutte le sale ({openOrders.length})
                    </button>
                    {rooms.map((r) => {
                        const count = openOrders.filter((o) => tableById[o.table_id]?.room_id === r.id).length;
                        return (
                            <button key={r.id} onClick={() => setActiveRoomId(r.id)}
                                data-testid={`room-tab-${r.id}`}
                                className={`h-8 px-3 rounded text-[11px] uppercase tracking-wider font-bold border ${
                                    activeRoomId === r.id ? "border-brand bg-brand/20 text-brand" : "border-[#1f2c44] text-elegant hover:border-brand/60"
                                }`}>
                                {r.name} {count > 0 && <span className="ml-1 px-1.5 py-0.5 rounded bg-amber-900/30 text-amber-300 text-[9px]">{count}</span>}
                            </button>
                        );
                    })}
                </div>
            )}

            {sortedOrders.length === 0 ? (
                <div className="bg-surface border border-[#1f2c44] rounded-lg p-10 text-center" data-testid="empty-state">
                    <Eye className="w-12 h-12 text-elegant/40 mx-auto mb-3" />
                    <div className="text-elegant">Nessun tavolo aperto in questa sala.</div>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="orders-list">
                    {sortedOrders.map((o) => {
                        const table = tableById[o.table_id];
                        const room = table?.room_id ? roomById[table.room_id] : null;
                        const total = orderTotal(o);
                        const activeItems = (o.items || []).filter((it) => it.status !== "annullato");
                        const billRequested = table?.status === "conto" || o.bill_requested_at;
                        const elapsed = elapsedMin(o.opened_at);
                        return (
                            <div key={o.id}
                                data-testid={`order-card-${o.table_id}`}
                                className={`bg-surface border rounded-lg p-4 ${
                                    billRequested ? "border-amber-500/60 ring-1 ring-amber-500/30" : "border-[#1f2c44]"
                                }`}>
                                <div className="flex items-start justify-between mb-3 gap-2">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <div className="font-display text-2xl font-bold">Tavolo {table?.number ?? "?"}</div>
                                            {room && <span className="text-[10px] uppercase tracking-wider text-elegant bg-deep border border-[#1f2c44] px-2 py-0.5 rounded">{room.name}</span>}
                                            {billRequested && <span className="text-[10px] uppercase tracking-wider bg-amber-500/20 text-amber-300 border border-amber-500/60 px-2 py-0.5 rounded animate-pulse">
                                                <ReceiptText className="w-3 h-3 inline -mt-0.5" /> Conto richiesto
                                            </span>}
                                        </div>
                                        <div className="text-[10px] text-elegant mt-1 flex items-center gap-2 flex-wrap">
                                            <span className="flex items-center gap-1"><ChefHat className="w-3 h-3" /> {o.waiter_name || "—"}</span>
                                            {o.covers > 0 && <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {o.covers}{o.covers_children > 0 && ` + ${o.covers_children}b`}</span>}
                                            <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> aperto da {elapsed} min</span>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-[10px] uppercase tracking-wider text-elegant">Totale</div>
                                        <div className="font-display text-2xl font-bold text-brand" data-testid={`order-total-${o.table_id}`}>€ {total.toFixed(2)}</div>
                                    </div>
                                </div>

                                {activeItems.length === 0 ? (
                                    <div className="text-xs text-elegant py-2 text-center bg-deep rounded">Nessun piatto inserito</div>
                                ) : (
                                    <div className="space-y-1 max-h-[280px] overflow-y-auto pr-1">
                                        {activeItems.map((it) => {
                                            const { gross, net } = itemNet(it);
                                            const hasDisc = (it.discount_pct || it.discount_amt) && Math.abs(gross - net) > 0.01;
                                            const sb = STATUS_BADGE[it.status] || STATUS_BADGE.in_attesa;
                                            const paid = !!it.paid_at;
                                            return (
                                                <div key={it.id}
                                                    className={`flex items-start justify-between gap-2 py-1.5 px-2 rounded border-b border-[#1f2c44]/40 last:border-b-0 text-xs ${
                                                        paid ? "opacity-50 line-through" : ""
                                                    }`}>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-1.5 flex-wrap">
                                                            <span className="font-mono text-brand">{it.quantity}×</span>
                                                            <span className="text-warm font-medium truncate">{it.name}</span>
                                                            <span className={`text-[8.5px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${sb.cls}`}>{sb.label}</span>
                                                            {paid && <span className="text-[8.5px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-900/40 text-emerald-200 border border-emerald-500/40">Pagato</span>}
                                                        </div>
                                                        {it.notes && <div className="text-[10px] text-elegant italic mt-0.5">📝 {it.notes}</div>}
                                                        {it.extras?.length > 0 && (
                                                            <div className="text-[10px] text-elegant mt-0.5">
                                                                {it.extras.map((ex) => `+ ${ex.label} (€${(ex.price || 0).toFixed(2)})`).join(", ")}
                                                            </div>
                                                        )}
                                                        {hasDisc && <div className="text-[10px] text-emerald-400 mt-0.5">Sconto applicato</div>}
                                                        {it.original_price && it.original_price !== it.price && (
                                                            <div className="text-[10px] text-amber-300 mt-0.5">Convenzione: € {it.original_price.toFixed(2)} → € {it.price.toFixed(2)}</div>
                                                        )}
                                                    </div>
                                                    <div className="text-right whitespace-nowrap font-mono">
                                                        {hasDisc && <div className="text-[10px] text-elegant line-through">€ {gross.toFixed(2)}</div>}
                                                        <div className="font-bold text-warm">€ {net.toFixed(2)}</div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {(o.discount_pct > 0 || o.discount_amt > 0) && (
                                    <div className="mt-2 text-[10px] text-amber-300 flex items-center gap-1">
                                        Sconto globale: {o.discount_pct > 0 ? `${o.discount_pct}%` : `€ ${(o.discount_amt || 0).toFixed(2)}`}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            <div className="text-center text-[10px] text-elegant pt-3 opacity-70">
                Aggiornamento automatico ogni 6 secondi · solo consultazione
            </div>
        </div>
    );
};

export default ContiTavoli;
